import axios from "axios";
import { getApiBaseUrl } from "./apiBase";

const BASE_URL = getApiBaseUrl();
const API_URL = `${BASE_URL}/`;

const AUTH_SKIP_REDIRECT = ["/auth/login", "/auth/signup", "/auth/refresh-token", "/refresh-token"];

export const authApiService = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: false,
});

const unwrapAuthPayload = (response) => {
  const root = response?.data || {};
  const body = root.body || {};
  const data = body.data || root.data || {};
  const statusCode = root.statusCode || response?.status;
  const success = root.success === true || statusCode === 200 || statusCode === 201;
  return {
    success,
    data,
    message: body.message || root.message,
  };
};

authApiService.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");

    if (token && !config.skipAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

authApiService.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || "";
    const shouldSkip = AUTH_SKIP_REDIRECT.some((path) => url.includes(path));
    const status = error.response?.status;
    const message = String(error.response?.data?.message || error.response?.data?.body?.message || "");
    const isAuthFailure =
      status === 401 ||
      (status === 403 && /token/i.test(message));

    if (isAuthFailure && !shouldSkip && window.location.pathname !== "/login") {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }

    return Promise.reject(error);
  }
);

export const authService = {
  async login(email, password) {
    try {
      const response = await authApiService.post("/auth/login", {
        emailid: email,
        userpassword: password,
      });

      const payload = unwrapAuthPayload(response);
      if (payload.success && payload.data?.accessToken) {
        const { accessToken, emailid, role, roleid, userid, profile, permissions } = payload.data;

        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("user", JSON.stringify({
          email: emailid,
          role: role,
          roleid: roleid,
          userid: userid,
          name: profile?.name || emailid?.split("@")[0] || "",
          permissions: permissions || {},
        }));
        localStorage.setItem("profile", JSON.stringify(profile));
        if (profile?.name) {
          localStorage.setItem("username", profile.name);
        }
        return {
          success: true,
          data: {
            accessToken,
            email: emailid,
            role: role,
            roleid: roleid,
            userid: userid,
            name: profile?.name || emailid?.split("@")[0] || "",
            profile,
            permissions: permissions || {},
          },
        };
      }

      return {
        success: false,
        message: payload.message || "Login failed",
      };
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.body?.message || error.response.data?.message || `Server error: ${error.response.status}`,
          status: error.response.status,
        };
      }
      if (error.request) {
        return {
          success: false,
          message: "No response from server. Check if server is running.",
        };
      }
      return {
        success: false,
        message: `Request error: ${error.message}`,
      };
    }
  },

  async signup(name, email, password) {
    try {
      const response = await authApiService.post("/auth/signup", {
        name,
        email,
        password,
      });

      const payload = unwrapAuthPayload(response);
      if (payload.success && payload.data?.accessToken) {
        localStorage.setItem("accessToken", payload.data.accessToken);
        localStorage.setItem("user", JSON.stringify({
          email: payload.data.email,
          name: payload.data.name,
        }));
        return { success: true, data: payload.data };
      }
      return { success: false, message: payload.message || "Signup failed" };
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.body?.message || error.response.data?.message || `Server error: ${error.response.status}`,
          status: error.response.status,
        };
      }
      if (error.request) {
        return { success: false, message: "No response from server" };
      }
      return { success: false, message: `Request error: ${error.message}` };
    }
  },

  async logout() {
    try {
      await authApiService.post("/auth/logout");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("user");
      return { success: true };
    } catch (error) {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("user");
      return { success: false, message: "Error during logout" };
    }
  },

  async refreshToken() {
    try {
      const response = await authApiService.post("/auth/refresh-token", {}, {
        skipAuth: true,
      });

      const payload = unwrapAuthPayload(response);
      if (payload.success && payload.data?.accessToken) {
        localStorage.setItem("accessToken", payload.data.accessToken);
        return { success: true, accessToken: payload.data.accessToken };
      }

      return { success: false };
    } catch (error) {
      console.error("Token refresh error:", error);
      return { success: false, networkError: !error.response };
    }
  },

  getCurrentUser() {
    const userStr = localStorage.getItem("user");
    if (!userStr) return null;

    try {
      return JSON.parse(userStr);
    } catch (error) {
      console.error("Error parsing user from localStorage", error);
      return null;
    }
  },

  isAuthenticated() {
    return !!localStorage.getItem("accessToken");
  },
};

export { unwrapAuthPayload };
